import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, XCircle, Loader2, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAgent } from '../../contexts/AgentContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useToast } from '../../hooks/use-toast';

const APPLIANCE_OPTIONS = [
  'Washing Machine', 'Tumble Dryer', 'Washer Dryer', 'Dishwasher', 'Fridge Freezer', 'American Fridge Freezer',
  'Cooker', 'Oven', 'Gas Hob', 'Electric Hob', 'Microwave', 'Extractor Hood', 'TV', 'Wine Cooler',
  'Boiler', 'Central Heating', 'Home Emergency', 'Plumbing & Drainage', 'Electrics',
];

const APPLIANCE_SLOTS = 7;

const NewPolicy = () => {
  const { logout } = useAgent();
  const { createPolicy } = usePlatform();
  const nav = useNavigate();
  const { toast } = useToast();
  const [f, setF] = useState({
    title: 'Mr', firstName: '', lastName: '',
    address1: '', address2: '', city: '', postcode: '',
    appliances: Array(APPLIANCE_SLOTS).fill(''),
    amount: '', policyStart: '', policyEnd: '', paymentDue: '',
    email: '', phone: '',
  });

  const [postcodeState, setPostcodeState] = useState({ status: 'idle', data: null, error: '' });
  const [saving, setSaving] = useState(false);

  const upd = (patch) => setF((s) => ({ ...s, ...patch }));
  const updAppliance = (i, v) => {
    const arr = [...f.appliances];
    arr[i] = v;
    upd({ appliances: arr });
  };

  const verifyPostcode = async () => {
    const pc = f.postcode.trim().toUpperCase();
    if (!pc) return setPostcodeState({ status: 'error', data: null, error: 'Enter a postcode first' });
    setPostcodeState({ status: 'loading', data: null, error: '' });
    try {
      const res = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      if (res.data.status === 200 && res.data.result) {
        const r = res.data.result;
        setPostcodeState({ status: 'ok', data: r, error: '' });
        // auto-fill city from result
        upd({ postcode: r.postcode, city: f.city || r.admin_district || r.parish || r.region || '' });
        toast({ title: 'Postcode verified', description: `${r.admin_district || r.region}, UK` });
      } else {
        setPostcodeState({ status: 'error', data: null, error: 'Not a valid UK postcode' });
      }
    } catch (e) {
      setPostcodeState({ status: 'error', data: null, error: 'Not a valid UK postcode' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.firstName || !f.lastName || !f.address1 || !f.postcode || !f.city) {
      return toast({ title: 'Please complete customer name and address' });
    }
    if (postcodeState.status !== 'ok') {
      return toast({ title: 'Postcode not verified', description: 'Please verify the postcode with Royal Mail before saving.' });
    }
    const selected = f.appliances.filter(Boolean);
    if (selected.length === 0) return toast({ title: 'Select at least one appliance' });
    if (!f.amount || Number(f.amount) <= 0) return toast({ title: 'Enter a valid amount in GBP' });
    if (!f.policyStart || !f.policyEnd || !f.paymentDue) return toast({ title: 'Please set all policy dates' });

    setSaving(true);
    setTimeout(() => {
      const policy = createPolicy({
        ...f,
        appliances: selected,
        amount: Number(f.amount),
      });
      toast({ title: 'Policy created', description: `Plan number ${policy.planNumber} generated.` });
      setSaving(false);
      nav(`/agent/policy/${policy.id}`);
    }, 400);
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA]">
      <header className="bg-[#0F0532] text-white">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/agent" className="flex items-center gap-2">
            <svg viewBox="0 0 40 40" className="w-8 h-8">
              <circle cx="20" cy="20" r="20" fill="#FF5C39" />
              <path d="M12 22 L18 28 L28 14" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="leading-tight"><div className="font-bold text-sm">Domestic Home Services</div><div className="text-[10px] tracking-[0.22em] text-white/60 uppercase">Agent Portal</div></div>
          </Link>
          <Button variant="ghost" onClick={() => { logout(); nav('/agent/login'); }} className="gap-1.5 text-white/85 hover:bg-white/10 rounded-full"><LogOut className="w-4 h-4" /> Sign out</Button>
        </div>
      </header>

      <main className="py-8">
        <div className="max-w-4xl mx-auto px-5">
          <Link to="/agent" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F0068]/70 hover:text-[#1F0068] mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight">New policy</h1>
          <p className="text-[#1F0068]/70 mt-1">Fill in the customer details. Plan number, invoice and paperwork are generated automatically.</p>

          <form onSubmit={submit} className="mt-6 space-y-6">
            {/* Personal */}
            <section className="bg-white rounded-2xl border border-[#1F0068]/5 p-6">
              <h2 className="font-bold text-[#1F0068] mb-4">Customer</h2>
              <div className="grid sm:grid-cols-[120px_1fr_1fr] gap-4">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Title</Label>
                  <Select value={f.title} onValueChange={(v) => upd({ title: v })}>
                    <SelectTrigger className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Mr', 'Mrs', 'Ms', 'Miss', 'Mx', 'Dr', 'Prof'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">First name</Label>
                  <Input value={f.firstName} onChange={(e) => upd({ firstName: e.target.value })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Last name</Label>
                  <Input value={f.lastName} onChange={(e) => upd({ lastName: e.target.value })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Email</Label>
                  <Input type="email" value={f.email} onChange={(e) => upd({ email: e.target.value })} placeholder="customer@example.com" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Phone</Label>
                  <Input value={f.phone} onChange={(e) => upd({ phone: e.target.value })} placeholder="07123 456789" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
              </div>
            </section>

            {/* Address */}
            <section className="bg-white rounded-2xl border border-[#1F0068]/5 p-6">
              <h2 className="font-bold text-[#1F0068] mb-4">Address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Address line 1</Label>
                  <Input value={f.address1} onChange={(e) => upd({ address1: e.target.value })} placeholder="House / building and street" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Address line 2</Label>
                  <Input value={f.address2} onChange={(e) => upd({ address2: e.target.value })} placeholder="Optional" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">City / Town</Label>
                  <Input value={f.city} onChange={(e) => upd({ city: e.target.value })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">UK Postcode</Label>
                  <div className="flex gap-2 mt-2">
                    <Input value={f.postcode} onChange={(e) => { upd({ postcode: e.target.value.toUpperCase() }); setPostcodeState({ status: 'idle', data: null, error: '' }); }}
                      placeholder="SW1A 2AA" className="h-11 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 flex-1" />
                    <Button type="button" onClick={verifyPostcode} className="h-11 px-4 rounded-xl bg-[#1F0068] hover:bg-[#2A0C7A] text-white text-sm font-semibold gap-1.5">
                      {postcodeState.status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                  {postcodeState.status === 'ok' && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-[#00b67a] font-semibold"><CheckCircle2 className="w-4 h-4" /> Verified with Royal Mail · {postcodeState.data.admin_district}</div>
                  )}
                  {postcodeState.status === 'error' && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-[#FF5C39] font-semibold"><XCircle className="w-4 h-4" /> {postcodeState.error}</div>
                  )}
                </div>
              </div>
            </section>

            {/* Appliances 7 boxes */}
            <section className="bg-white rounded-2xl border border-[#1F0068]/5 p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-[#1F0068]">Appliances covered</h2>
                <span className="text-xs text-[#1F0068]/60">Up to {APPLIANCE_SLOTS} items</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {Array.from({ length: APPLIANCE_SLOTS }).map((_, i) => (
                  <div key={i}>
                    <Label className="text-[#1F0068] font-semibold text-xs">Appliance {i + 1}</Label>
                    <Select value={f.appliances[i] || undefined} onValueChange={(v) => updAppliance(i, v === '__none__' ? '' : v)}>
                      <SelectTrigger className="h-11 mt-1.5 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue placeholder={`Select appliance ${i + 1}`} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {APPLIANCE_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </section>

            {/* Financials */}
            <section className="bg-white rounded-2xl border border-[#1F0068]/5 p-6">
              <h2 className="font-bold text-[#1F0068] mb-4">Policy & payment</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Amount (GBP)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/60 font-bold">£</span>
                    <Input type="number" step="0.01" value={f.amount} onChange={(e) => upd({ amount: e.target.value })} placeholder="189.00" className="h-11 pl-8 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Policy start date</Label>
                  <Input type="date" value={f.policyStart} onChange={(e) => upd({ policyStart: e.target.value })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Policy end date</Label>
                  <Input type="date" value={f.policyEnd} onChange={(e) => upd({ policyEnd: e.target.value })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Payment due date</Label>
                  <Input type="date" value={f.paymentDue} onChange={(e) => upd({ paymentDue: e.target.value })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
              </div>
            </section>

            <div className="flex items-center justify-between gap-3 pt-4 pb-10">
              <Link to="/agent"><Button type="button" variant="ghost" className="rounded-full text-[#1F0068]">Cancel</Button></Link>
              <Button type="submit" disabled={saving} className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white h-12 px-8 gap-1.5 font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Create policy & generate paperwork</>}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewPolicy;
